"use client";

import { FadeIn } from "@/components/ui/motion";
import { Coins, Cpu, ShieldCheck, Users, Zap } from "lucide-react";

export function WhyUs() {
    const comparisonRows = [
        {
            icon: Users,
            metric: "Team Quality",
            agency: "Junior developers / Project Manager layers",
            dharmatech: "Elite Senior Engineers + AI co-pilots",
            freelancer: "Solo developers / Variable expertise",
        },
        {
            icon: Zap,
            metric: "Delivery Speed",
            agency: "Slow, heavy process overhead (Months)",
            dharmatech: "AI-Accelerated Fast MVPs (Weeks)",
            freelancer: "Highly unpredictable schedules",
        },
        {
            icon: ShieldCheck,
            metric: "IP & Code Ownership",
            agency: "Risk of vendor lock-in & IP disputes",
            dharmatech: "100% Client-Owned from Day 1",
            freelancer: "Vague contracts / Unclear rights",
        },
        {
            icon: Cpu,
            metric: "Tech Stack & Innovation",
            agency: "Legacy structures and outdated boilerplates",
            dharmatech: "Agentic AI workflows & modern frameworks",
            freelancer: "Varies wildly per developer",
        },
        {
            icon: Coins,
            metric: "Pricing & Transparency",
            agency: "Hidden fees & complex contracts",
            dharmatech: "Transparent, value-aligned pricing",
            freelancer: "Risk of scope creep & billing disputes",
        }
    ];

    return (
        <section className="py-24 bg-zinc-50 relative overflow-hidden">
            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">Why DharmaTech?</h2>
                        <p className="text-lg text-gray-600">
                             We bridge the gap between <span className="text-black font-bold">vision</span> and <span className="text-black font-bold">execution</span>. Here is a side-by-side comparison of how we stack up.
                        </p>
                    </div>
                </FadeIn>

                <FadeIn>
                    <div className="max-w-6xl mx-auto mt-16">
                        {/* Comparison Table Card Container */}
                        <div className="bg-white border border-zinc-200/80 rounded-3xl p-6 md:p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[768px] border-collapse text-left">
                                    <thead>
                                        <tr className="border-b-2 border-gray-900">
                                            <th className="py-4 pr-4 text-xs font-bold uppercase tracking-wider text-gray-400 w-1/4">Metric</th>
                                            <th className="py-4 px-4 text-xs font-bold uppercase tracking-wider text-gray-400 w-1/4">Traditional Agency</th>
                                            <th className="py-4 px-4 text-xs font-bold uppercase tracking-wider text-black w-1/4">
                                                <span className="normal-case">DharmaTech</span> <span className="ml-1.5 text-[9px] font-bold tracking-widest text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded uppercase">Elite</span>
                                            </th>
                                            <th className="py-4 px-4 text-xs font-bold uppercase tracking-wider text-gray-400 w-1/4">Freelancers</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {comparisonRows.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="py-5 pr-4 font-semibold text-gray-900 text-sm">
                                                    <span className="flex items-center gap-2.5">
                                                        <row.icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                        {row.metric}
                                                    </span>
                                                </td>
                                                <td className="py-5 px-4 text-gray-500 text-sm leading-relaxed">{row.agency}</td>
                                                <td className="py-5 px-4 font-semibold text-black text-sm leading-relaxed">
                                                    {row.dharmatech}
                                                </td>
                                                <td className="py-5 px-4 text-gray-500 text-sm leading-relaxed">{row.freelancer}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}
